import React, { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import { IconButton, InputAdornment } from '@mui/material';
import { useDispatch } from 'react-redux';
import Lock from '@mui/icons-material/Lock';
import FormValidations from 'Utils/FormValidations';

import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import useLogOut from 'Hooks/useLogOut';
import useInput from '../../Hooks/useInput';
import useTranslation from '../../Hooks/useTranslation';
import Styles from './styles';
import CustomLabel from '../../Utils/CustomLabel';
import { setFirstPassword } from '../../Redux/LoggedUserSlice';
import { getPassword } from '../../Utils/Password';
import PasswordEye from './Components/PasswordEye';

export default function index() {
  const { t2 } = useTranslation('ChangePassword');
  const originalPassword = getPassword('password');
  const oldPassword = useInput('');
  const newPassword = useInput('');
  const repeatNewPassword = useInput('');
  const dispatch = useDispatch();

  // Variables para poder visualizar o no la contraseña del usuario.
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showRepeatNewPassword, setShowRepeatNewPassword] = useState(false);

  const clearError = () => {
    oldPassword.setError(false);
    oldPassword.setErrorMessage('');
    newPassword.setError(false);
    newPassword.setErrorMessage('');
    repeatNewPassword.setError(false);
    repeatNewPassword.setErrorMessage('');
  };

  const changePasswordAPI = async () => {
    clearError();

    // Validación que tenga valor la contraseña anterior
    if (!oldPassword.value) {
      oldPassword.setError(true);
      oldPassword.setErrorMessage(t2('emptyOldPassword'));
      return;
    }

    // Validamos que la contraseña anterior sea correcta
    if (oldPassword.value !== originalPassword) {
      oldPassword.setError(true);
      oldPassword.setErrorMessage(t2('oldPasswordNotMatch'));
      return;
    }

    // Validamos que la contraseña nueva y la repetición sean iguales
    if (newPassword.value !== repeatNewPassword.value) {
      repeatNewPassword.setError(true);
      repeatNewPassword.setErrorMessage(t2('passwordNotMatch'));
      return;
    }

    // Validamos las características que debe tener la contraseña
    const newErrorMessage = FormValidations.password(newPassword.value);
    const repeatErrorMessage = FormValidations.password(repeatNewPassword.value);

    if (newErrorMessage !== '') {
      newPassword.setError(true);
      newPassword.setErrorMessage(newErrorMessage);
      return;
    }

    if (repeatErrorMessage !== '') {
      repeatNewPassword.setError(true);
      repeatNewPassword.setErrorMessage(repeatErrorMessage);
      return;
    }

    // Validamos que la contraseña nueva no sea igual a la anterior
    const sameAsOld = [newPassword.value, repeatNewPassword.value].includes(originalPassword);

    if (sameAsOld) {
      newPassword.setError(true);
      newPassword.setErrorMessage('La contraseña nueva no puede ser igual a la anterior.');
      return;
    }

    dispatch(
      setFirstPassword({
        old: oldPassword.value,
        new: newPassword.value,
        clearErrors: () => {
          oldPassword.setError(false);
          oldPassword.setErrorMessage('');
          newPassword.setError(false);
          newPassword.setErrorMessage('');
          repeatNewPassword.setError(false);
          repeatNewPassword.setErrorMessage('');
        },
        catchError: errorMessage => {
          newPassword.setError(true);
          newPassword.setErrorMessage(errorMessage);
        },
      })
    );
  };

  const { logOutApi } = useLogOut();

  return (
    <div style={Styles.container} className="flex col">
      <CustomLabel type="h1" value={t2('header')} style={Styles.header} />

      <TextField
        label={t2('oldPassword')}
        color="MexlogGreen"
        placeholder="********"
        type={showOldPassword ? 'text' : 'password'}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock />
            </InputAdornment>
          ),
          endAdornment: (
            <PasswordEye visibility={showOldPassword} onClick={() => setShowOldPassword(!showOldPassword)} />
          ),
        }}
        style={Styles.textField}
        {...oldPassword.bind}
      />

      <TextField
        label={t2('newPassword')}
        color="MexlogGreen"
        placeholder="********"
        type={showNewPassword ? 'text' : 'password'}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock />
            </InputAdornment>
          ),
          endAdornment: (
            <PasswordEye visibility={showNewPassword} onClick={() => setShowNewPassword(!showNewPassword)} />
          ),
        }}
        style={Styles.textField}
        {...newPassword.bind}
      />

      <TextField
        label={t2('repeatPassword')}
        color="MexlogGreen"
        placeholder="********"
        type={showRepeatNewPassword ? 'text' : 'password'}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock />
            </InputAdornment>
          ),
          endAdornment: (
            <PasswordEye
              visibility={showRepeatNewPassword}
              onClick={() => setShowRepeatNewPassword(!showRepeatNewPassword)}
            />
          ),
        }}
        style={Styles.textField}
        {...repeatNewPassword.bind}
      />

      <Button onClick={changePasswordAPI} style={Styles.changePasswordButton}>
        {t2('changePasswordButton')}
      </Button>

      <Box style={Styles.backToLogin} onClick={logOutApi}>
        <CustomLabel type="link" style={Styles.backToLoginText} value={t2('backToLogin')} redirect="/login" />
      </Box>
    </div>
  );
}
